import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CalendarCheck, CheckCircle2, Clock, XCircle } from "lucide-react";

const STATUSES = [
  { key: "present", label: "Present", icon: CheckCircle2, color: "bg-emerald-500/15 text-emerald-500" },
  { key: "late", label: "Late", icon: Clock, color: "bg-amber-500/15 text-amber-500" },
  { key: "absent", label: "Absent", icon: XCircle, color: "bg-red-500/15 text-red-500" },
];

export default function AttendancePage() {
  const { user } = useAuth();
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState("");
  const [students, setStudents] = useState([]);
  const [marks, setMarks] = useState({});
  const [summary, setSummary] = useState(null);
  const [myAttendance, setMyAttendance] = useState([]);

  const isStaff = ["admin", "principal", "teacher"].includes(user?.role);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/classes");
      setClasses(data);
      if (data[0]) setClassId(data[0].id);
    })();
  }, []);

  useEffect(() => {
    if (!classId) return;
    (async () => {
      const [s, a] = await Promise.all([
        api.get("/users", { params: { role: "student", class_id: classId } }),
        api.get("/attendance", { params: { class_id: classId, date } }),
      ]);
      setStudents(s.data);
      const initial = {};
      s.data.forEach((st) => {
        const found = a.data.find((x) => x.student_id === st.id);
        initial[st.id] = found?.status || "present";
      });
      setMarks(initial);
    })();
  }, [classId, date]);

  useEffect(() => {
    if (user?.role !== "student" && user?.role !== "parent") return;
    (async () => {
      const sid = user.role === "student" ? user.id : null;
      if (!sid) return;
      const [sum, list] = await Promise.all([
        api.get("/attendance/summary", { params: { student_id: sid } }),
        api.get("/attendance", { params: { student_id: sid } }),
      ]);
      setSummary(sum.data);
      setMyAttendance(list.data.slice(0, 20));
    })();
  }, [user]);

  const setStatus = (sid, status) => setMarks((m) => ({ ...m, [sid]: status }));

  const save = async () => {
    const entries = Object.entries(marks).map(([student_id, status]) => ({ student_id, status }));
    try {
      await api.post("/attendance/mark", { class_id: classId, date, entries });
      toast.success(`Attendance saved for ${entries.length} learners`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    }
  };

  if (!isStaff) {
    return (
      <div className="space-y-6" data-testid="attendance-page">
        <div>
          <div className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">Attendance</div>
          <h1 className="text-4xl font-bold tracking-tight">Your attendance</h1>
        </div>
        <div className="grid gap-4 md:grid-cols-4">
          <Card className="rounded-2xl"><CardContent className="p-6"><div className="text-xs uppercase text-muted-foreground tracking-widest">Attendance %</div><div className="text-3xl font-bold mt-2">{summary?.attendance_pct ?? 0}%</div></CardContent></Card>
          <Card className="rounded-2xl"><CardContent className="p-6"><div className="text-xs uppercase text-muted-foreground tracking-widest">Present</div><div className="text-3xl font-bold mt-2 text-emerald-500">{summary?.present ?? 0}</div></CardContent></Card>
          <Card className="rounded-2xl"><CardContent className="p-6"><div className="text-xs uppercase text-muted-foreground tracking-widest">Late</div><div className="text-3xl font-bold mt-2 text-amber-500">{summary?.late ?? 0}</div></CardContent></Card>
          <Card className="rounded-2xl"><CardContent className="p-6"><div className="text-xs uppercase text-muted-foreground tracking-widest">Absent</div><div className="text-3xl font-bold mt-2 text-red-500">{summary?.absent ?? 0}</div></CardContent></Card>
        </div>
        <Card className="rounded-2xl">
          <CardHeader><CardTitle className="text-base">Recent</CardTitle></CardHeader>
          <CardContent className="divide-y divide-border/60">
            {myAttendance.map((a) => (
              <div key={a.id} className="py-3 flex items-center gap-3">
                <CalendarCheck className="h-4 w-4 text-muted-foreground" />
                <div className="flex-1">{a.date}</div>
                <Badge className={`rounded-full ${STATUSES.find((s) => s.key === a.status)?.color}`}>{a.status}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="attendance-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">Register</div>
          <h1 className="text-4xl font-bold tracking-tight">Mark attendance</h1>
          <p className="text-muted-foreground text-sm mt-1">Select a class and mark today's register.</p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <Select value={classId} onValueChange={setClassId}>
            <SelectTrigger data-testid="attendance-class-select" className="w-48 rounded-2xl"><SelectValue placeholder="Select class" /></SelectTrigger>
            <SelectContent>
              {classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <input data-testid="attendance-date" type="date" value={date} onChange={(e) => setDate(e.target.value)}
            className="h-10 rounded-2xl px-3 border border-border bg-input text-foreground text-sm" />
          <Button data-testid="attendance-save" onClick={save} className="rounded-2xl">Save register</Button>
        </div>
      </div>

      <Card className="rounded-2xl">
        <CardHeader><CardTitle className="text-base">Learners</CardTitle></CardHeader>
        <CardContent className="divide-y divide-border/60">
          {students.length === 0 && <div className="py-8 text-center text-sm text-muted-foreground">No learners in this class.</div>}
          {students.map((s) => (
            <div key={s.id} className="py-3 flex items-center gap-3" data-testid={`attendance-row-${s.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{s.name}</div>
                <div className="text-xs text-muted-foreground truncate">Grade {s.grade || "—"}</div>
              </div>
              <div className="flex gap-1">
                {STATUSES.map((st) => {
                  const active = marks[s.id] === st.key;
                  return (
                    <Button
                      key={st.key}
                      variant={active ? "default" : "outline"}
                      size="sm"
                      onClick={() => setStatus(s.id, st.key)}
                      className={`rounded-xl h-8 gap-1 ${active ? "" : "hover:bg-muted"}`}
                      data-testid={`mark-${s.id}-${st.key}`}
                    >
                      <st.icon className="h-3.5 w-3.5" /> {st.label}
                    </Button>
                  );
                })}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
